import React, { useState, useMemo, useEffect } from "react";
import {
  BackArrowIcon,
  DownArrowSelect,
  DownloadExportIcon,
  PaginationLeftArrow,
  PaginationRightArrow,
  RefreshWhiteIcon,
} from "../../assets/icons";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import { reqToGetDiscountUsageReports } from "../../redux/slices.js/reports/reportsSlice";
import { useDispatch, useSelector } from "react-redux";
import Table from "../../components/Tables";
import PaginationTab from "../../components/PaginationTab";

const { RangePicker } = DatePicker;

const planOptions = [
  { value: "all", label: "All plans" },
  { value: "starter", label: "Starter" },
  { value: "growth", label: "Growth" },
];

const tenantSummaryOptions = [
  { value: "all", label: "All Tenants" },
  { value: "active", label: "Active" },
  { value: "trial", label: "Trial" },
];

const Reports = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState(planOptions[0]);
  const [selectedSummary, setSelectedSummary] = useState(
    tenantSummaryOptions[0]
  );
  const [selectedDate, setSelectedDate] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [openRow, setOpenRow] = useState(null);
  const dropdownRef = React.useRef(null);
  const dispatch = useDispatch();

  const discountUsageReports = useSelector(
    (state) => state.reposrtSlice.discountUsageReports
  );

  useEffect(() => {
    dispatch(
      reqToGetDiscountUsageReports({
        data: { page: currentPage, limit: pageSize },
        onSuccess: () => {},
        onFailure: () => {},
      })
    );
  }, [dispatch, currentPage, pageSize]);

  // Transform the data for table display
  const transformedData = useMemo(() => {
    if (!discountUsageReports || !Array.isArray(discountUsageReports))
      return [];

    return discountUsageReports.map((report) => ({
      id: report.id,
      company: report.tenant?.name || "N/A",
      plan: report.discountProgram?.name || "N/A",
      status: report.status || "N/A",
      user: report.tenant?.name || "N/A", // You might want to adjust this based on actual data structure
      mrr: `$${report.applied_amount || "0"}`,
      createdOn: report.applied_at
        ? dayjs(report.applied_at).format("YYYY-MM-DD")
        : "N/A",
      // Additional fields from your data
      discountProgram: report.discountProgram,
      discountCode: report.discountcode,
      appliedAmount: report.applied_amount,
      source: report.source,
      remainingCycles: report.remaining_cycles,
      auditData: report.audit_json,
    }));
  }, [discountUsageReports]);

  // Filter tenants
  const filteredTenants = useMemo(() => {
    let result = transformedData;

    // Plan filter
    if (selectedPlan.value !== "all") {
      result = result.filter(
        (t) => t.plan.toLowerCase() === selectedPlan.value
      );
    }

    // Summary filter
    if (selectedSummary.value === "active") {
      result = result.filter((t) => t.status === "Active");
    } else if (selectedSummary.value === "trial") {
      result = result.filter((t) => t.status === "Trial");
    }

    // Date filter
    if (selectedDate.length === 2) {
      const [start, end] = selectedDate;
      result = result.filter((t) => {
        const createdDate = dayjs(t.createdOn);
        return (
          createdDate.isAfter(start, "day") && createdDate.isBefore(end, "day")
        );
      });
    }

    return result;
  }, [selectedPlan, selectedSummary, selectedDate, transformedData]);

  const totalCount = filteredTenants.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  const paginatedTenants = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredTenants.slice(start, start + pageSize);
  }, [filteredTenants, currentPage, pageSize]);

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const goToPage = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    setCurrentPage(pageNum);
  };

  // Define table columns based on your data structure
  const columns = [
    {
      key: "company",
      dataIndex: "company",
      title: "Company",
      render: (value, record) => <span className="font-semibold">{value}</span>,
    },
    {
      key: "plan",
      dataIndex: "plan",
      title: "Plan",
      render: (value, record) => (
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            value === "Starter" || value === "growth"
              ? "bg-[#F3F7FF] text-[#4285F4]"
              : "bg-[#FAFAFA] text-[#5C5B62]"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "status",
      dataIndex: "status",
      title: "Status",
      render: (value, record) => (
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            value === "APPLIED" || value === "Active"
              ? "bg-[#E9FFDF] text-[#52C41A]"
              : value === "trial"
              ? "bg-[#F3F7FF] text-[#4285F4]"
              : "bg-[#FFEFEE] text-[#EA4335]"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "user",
      dataIndex: "user",
      title: "User",
      render: (value, record) => <span className="font-medium">{value}</span>,
    },
    {
      key: "appliedAmount",
      dataIndex: "appliedAmount",
      title: "Discount Applied",
      render: (value, record) => (
        <span className="font-semibold">${value || "0"}</span>
      ),
    },
    {
      key: "source",
      dataIndex: "source",
      title: "Source",
      render: (value, record) => (
        <span
          className={`rounded-full px-3 py-1 text-xs font-medium ${
            value === "COUPON"
              ? "bg-[#F3F7FF] text-[#4285F4]"
              : "bg-[#F8FFCF] text-[#4285F4]"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "createdOn",
      dataIndex: "createdOn",
      title: "Applied On",
      render: (value, record) => <span className="font-medium">{value}</span>,
    },
  ];

  // Row actions configuration (similar to CodesTab)
  const rowActions = {
    threeDotsIcon: null, // You can add your three dots icon here
    actions: (row) => [
      {
        label: "View Details",
        icon: null, // Add your ViewDetailsIcon here
        to: "#",
        onClick: (row, index) => {
          // Handle view details action
        },
      },
      {
        label: "Export Report",
        icon: null, // Add your export icon here
        to: "#",
        onClick: (row, index) => {
          // Handle export action
        },
      },
    ],
  };

  return (
    <div className="rounded-md border-[1px] border-solid border-[#F3EFEF] space-y-8 pb-6">
      {/* Header */}
      <div className="p-3 sm:p-6 bg-[#F5FFBD] space-y-6">
        <div className="mb-4">
          <button
            type="button"
            className="text-[16px] font-normal flex gap-1"
            onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 1))}
          >
            {BackArrowIcon} Back
          </button>
        </div>
        <div className="heading flex gap-2 justify-between items-end">
          <div className="heading-title space-y-1">
            <h1 className="text-h2 !font-medium mb-0 text-[#4285F4]">
              Discount Usage Reports
            </h1>
            <p className="text-span text-dark mb-0">
              View and analyze discount usage across all tenants
            </p>
          </div>
        </div>
      </div>

      <div className="sm:p-6 p-3 !pt-0">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div className="chart-card !shadow-none">
            <p className="mb-2 text-sm">Total Reports</p>
            <p className="text-blue-600 text-xl font-bold mb-0">{totalCount}</p>
          </div>
          <div className="chart-card !shadow-none">
            <p className="mb-2 text-sm">Applied Discounts</p>
            <p className="text-blue-600 text-xl font-bold mb-0">
              {filteredTenants.filter((t) => t.status === "APPLIED").length}
            </p>
          </div>
          <div className="chart-card !shadow-none">
            <p className="mb-2 text-sm">Total Discount Amount</p>
            <p className="text-blue-600 text-xl font-bold mb-0">
              $
              {filteredTenants
                .reduce((sum, t) => sum + parseFloat(t.appliedAmount || 0), 0)
                .toFixed(2)}
            </p>
          </div>
          <div className="chart-card !shadow-none">
            <p className="mb-2 text-sm">Coupon Usage</p>
            <p className="text-blue-600 text-xl font-bold mb-0">
              {filteredTenants.filter((t) => t.source === "COUPON").length}
            </p>
          </div>
          <div className="chart-card !shadow-none">
            <p className="mb-2 text-sm">Rule Usage</p>
            <p className="text-blue-600 text-xl font-bold mb-0">
              {filteredTenants.filter((t) => t.source === "RULE").length}
            </p>
          </div>
          <div className="chart-card !shadow-none">
            <p className="mb-2 text-sm">Active Tenants</p>
            <p className="text-blue-600 text-xl font-bold mb-0">
              {filteredTenants.filter((t) => t.status === "Active").length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="chart-card mt-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:mb-4 mb-3 flex-wrap">
            <div className="flex gap-2 sm:flex-row flex-col w-auto">
              <div className="w-full sm:w-40">
                <Select
                  value={selectedSummary}
                  onChange={(option) => setSelectedSummary(option)}
                  options={tenantSummaryOptions}
                  className="text-sm w-full"
                  placeholder="Tenant Summary"
                />
              </div>
              <div className="w-full sm:w-60">
                <RangePicker
                  onChange={(dates) => setSelectedDate(dates)}
                  className="w-full"
                />
              </div>
            </div>

            <div className="heading-btns flex items-center gap-3 flex-wrap">
              <button className="btn-light-blue">
                {RefreshWhiteIcon} Refresh
              </button>
              <button className="btn-light-gray">
                {DownloadExportIcon}Export Excel
              </button>
              <button className="btn-light-gray">
                {DownloadExportIcon} Export PDF
              </button>
            </div>
          </div>
          <div className="flex flex-col xl:flex-row md:items-start gap-4 md:justify-between bg-[#EDF7FF] rounded-xl sm:p-6 p-3 mb-6">
            {/* ICON & DETAILS */}
            <div className="flex items-center gap-2">
              <span className="min-w-[40px]">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="40"
                  height="40"
                  viewBox="0 0 40 40"
                  fill="none"
                >
                  <rect width="40" height="40" rx="20" fill="#4285F4"></rect>
                  <path
                    d="M20.0003 28.3337C24.6027 28.3337 28.3337 24.6027 28.3337 20.0003C28.3337 15.398 24.6027 11.667 20.0003 11.667C15.398 11.667 11.667 15.398 11.667 20.0003C11.667 24.6027 15.398 28.3337 20.0003 28.3337Z"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M20 23.333V19.583"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                  <path
                    d="M20 16.6763V16.668"
                    stroke="white"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></path>
                </svg>
              </span>
              <div>
                <span className="text-[16px] font-semibold mb-2 block">
                  Report Generated
                </span>
                <p className="text-gray-500 text-sm mb-0">
                  Showing discount usage reports for{" "}
                  {selectedDate.length === 2
                    ? `${dayjs(selectedDate[0]).format(
                        "MMM D, YYYY"
                      )} to ${dayjs(selectedDate[1]).format("MMM D, YYYY")}`
                    : "all dates"}
                </p>
              </div>
            </div>
          </div>

          {/* Dynamic Table Component */}
          <Table
            columns={columns}
            data={paginatedTenants}
            keyField="id"
            rowActions={rowActions}
            openRow={openRow}
            setOpenRow={setOpenRow}
            dropdownRef={dropdownRef}
            emptyMessage="No discount usage reports found"
            onRowClick={(row) => {
              console.log("Row clicked:", row);
            }}
          />

          {/* Pagination Component */}
          <PaginationTab
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </div>
    </div>
  );
};

export default Reports;
